/* eslint-disable react/prop-types */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRolePermissions } from '@app/shared/hooks/useRolePermissions';
import { Row, Col, Card } from 'react-bootstrap';
import Typography from '@mui/material/Typography';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import {
  GovernanceGateChart,
  SensitiveAttributesFrequencyPanel,
  ComplianceVsAuditChart,
  VerdictBarChart
} from './BiasRecharts';
import type { GovernanceGateDataItem, GovernanceGateOverlay, SensitiveAttributeTriggerItem } from './BiasRecharts';
import { BIAS_GRAPH_BLUE, BIAS_GRAPH_GREEN, BIAS_GRAPH_RED } from './biasLlmChartPalette';
import BiasLlmRecordsTable, { type LlmBiasTableRow } from './BiasLlmRecordsTable';
import { pickBestAggregateSummary } from '../../../utils/aggregateSummaryPick';
import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { metricScalarFromEntry } from './biasSummaryReference.utils';
import { isNonEmptyRecord, mergeBiasAggregateSummaries } from './biasAggregateSummary.utils';

/**
 * Shared section that renders the Bias LLM plots (same as Bias Tab).
 * Used in Review 360 tab and anywhere else that needs these charts.
 * Data is derived from projectResults (Redux) aggregate_summary.
 * When `customSummary` is provided (e.g. from the 360-analysis API), it takes priority.
 */
interface Props {
  /** Pre-aggregated bias summary from the 360-analysis API. Takes priority over Redux data. */
  customSummary?: Record<string, any>;
  /** Per-request rows when the parent has them (e.g. bias tab). Otherwise we try Redux `bias_llm` / `bias`. */
  requestRecords?: LlmBiasTableRow[];
}

/** First LLM bias `data` array found under project results (same buckets as aggregate summary). */
function extractFirstLlmRequestData(projectResults: unknown): LlmBiasTableRow[] | null {
  if (!projectResults || typeof projectResults !== 'object') return null;
  const pr = projectResults as Record<string, unknown>;
  const roots = [pr.results, pr.feature_results, pr.collection_results].filter(
    (x): x is Record<string, unknown> => x != null && typeof x === 'object'
  );
  for (const root of roots) {
    const llm = root.bias_llm ?? root.bias;
    const item = pickLatestCollectionRun(Array.isArray(llm) ? llm : llm ? [llm] : []);
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const data = rec.data ?? rec.entries;
    if (!Array.isArray(data) || data.length === 0) continue;
    const first = data[0];
    if (!first || typeof first !== 'object') continue;
    const row = first as Record<string, unknown>;
    if ('bias_analysis' in row || 'Entry_Label' in row || 'prompt' in row) {
      return data as LlmBiasTableRow[];
    }
  }
  return null;
}

const BiasLLMPlotsSection: React.FC<Props> = ({ customSummary, requestRecords }) => {
  const projectResults = useSelector(selectProjectResults);
  const { isDeveloper } = useRolePermissions();
  const softPalette = isDeveloper;

  const reduxAggregateSummary = useMemo(() => {
    if (!projectResults || typeof projectResults !== 'object') {
      return undefined;
    }
    const fromBiasArray = (arr: unknown) => {
      const item = pickLatestCollectionRun(arr as { aggregate_summary?: unknown }[] | null | undefined);
      if (!item || typeof item !== 'object') return undefined;
      const agg = (item as Record<string, unknown>).aggregate_summary;
      return agg !== undefined && agg !== null && typeof agg === 'object'
        ? (agg as Record<string, unknown>)
        : undefined;
    };
    const direct = pickBestAggregateSummary([
      projectResults?.results?.aggregate_summary,
      projectResults?.results?.bias?.aggregate_summary,
      fromBiasArray(projectResults?.results?.bias),
      projectResults?.results?.bias_llm?.aggregate_summary,
      fromBiasArray(projectResults?.results?.bias_llm),
      projectResults?.feature_results?.aggregate_summary,
      projectResults?.feature_results?.bias?.aggregate_summary,
      fromBiasArray(projectResults?.feature_results?.bias),
      projectResults?.feature_results?.bias_llm?.aggregate_summary,
      fromBiasArray(projectResults?.feature_results?.bias_llm),
      projectResults?.collection_results?.aggregate_summary,
      projectResults?.collection_results?.bias?.aggregate_summary,
      fromBiasArray(projectResults?.collection_results?.bias),
      projectResults?.collection_results?.bias_llm?.aggregate_summary,
      fromBiasArray(projectResults?.collection_results?.bias_llm)
    ]);
    if (direct) return direct;

    // Resolve when bias is under alternate keys or is an array (e.g. collection_results.bias = [{ aggregate_summary }])
    const candidateSources = [
      projectResults?.results,
      projectResults?.feature_results,
      projectResults?.collection_results
    ].filter(Boolean) as Record<string, unknown>[];

    const llmKeys = ['bias', 'bias_llm', 'LLMBiasDetection', 'llmBiasDetection', 'llm_bias_detection', 'BiasLLM'];

    for (const source of candidateSources) {
      for (const key of llmKeys) {
        const bucket = source?.[key];
        if (Array.isArray(bucket) && bucket.length > 0) {
          const latest = pickLatestCollectionRun(bucket) as Record<string, unknown> | null;
          if (latest?.aggregate_summary && typeof latest.aggregate_summary === 'object') {
            return latest.aggregate_summary as Record<string, unknown>;
          }
        } else if (bucket && typeof bucket === 'object' && (bucket as Record<string, unknown>)?.aggregate_summary) {
          const agg = (bucket as Record<string, unknown>).aggregate_summary;
          if (agg && typeof agg === 'object') return agg as Record<string, unknown>;
        }
      }
      if (source && typeof source === 'object' && 'aggregate_summary' in source) {
        const agg = (source as Record<string, unknown>).aggregate_summary;
        if (agg && typeof agg === 'object') return agg as Record<string, unknown>;
      }
    }
    return undefined;
  }, [projectResults]);

  const globalAggregateSummary: Record<string, any> | undefined = useMemo(
    () =>
      mergeBiasAggregateSummaries(isNonEmptyRecord(customSummary) ? customSummary : undefined, reduxAggregateSummary),
    [customSummary, reduxAggregateSummary]
  );

  const verdictData = useMemo(() => {
    const summary = globalAggregateSummary;
    const verdictDistribution = summary?.verdict_distribution;
    if (!verdictDistribution || typeof verdictDistribution !== 'object') {
      return null;
    }
    const verdicts: { key: 'APPROVE' | 'REVIEW' | 'REJECT'; color: string }[] = isDeveloper
      ? [
          { key: 'APPROVE', color: BIAS_GRAPH_BLUE },
          { key: 'REVIEW', color: BIAS_GRAPH_BLUE },
          { key: 'REJECT', color: BIAS_GRAPH_BLUE }
        ]
      : [
          { key: 'APPROVE', color: BIAS_GRAPH_GREEN },
          { key: 'REVIEW', color: '#f59e0b' },
          { key: 'REJECT', color: BIAS_GRAPH_RED }
        ];
    return verdicts.map(({ key, color }) => ({
      name: key,
      value: verdictDistribution[key] ?? 0,
      color
    }));
  }, [globalAggregateSummary, isDeveloper]);

  const complianceVsAuditData = useMemo(() => {
    const summary = globalAggregateSummary;
    const avgMetrics = summary?.avg_metrics;
    const auditMetrics = summary?.audit_avg_metrics || summary?.audited_avg_metrics;
    if (!avgMetrics || typeof avgMetrics !== 'object') return null;
    const metricKeys = ['WEAT_Avg', 'StereoSet', 'CrowsPairs', 'Toxicity'] as const;
    const categories = {
      WEAT_Avg: 'Association Skew',
      StereoSet: 'Stereotype Score',
      CrowsPairs: 'Group Favoritism',
      Toxicity: 'Hostility Score'
    } as const;
    const hasAuditData = auditMetrics && typeof auditMetrics === 'object';
    return metricKeys.map(key => ({
      metric: categories[key],
      compliance: Number(Number(metricScalarFromEntry(avgMetrics[key]) ?? 0).toFixed(3)),
      audit: hasAuditData ? Number(Number(metricScalarFromEntry(auditMetrics?.[key]) ?? 0).toFixed(3)) : null
    }));
  }, [globalAggregateSummary]);

  const governanceGateData = useMemo((): GovernanceGateDataItem[] | null => {
    const summary = globalAggregateSummary;
    const funnel = summary?.funnel;
    if (!funnel || typeof funnel !== 'object') return null;
    return [
      {
        label: 'Sensitive Detected',
        value: funnel.sensitive_detected?.count ?? 0,
        color: BIAS_GRAPH_BLUE
      },
      {
        label: 'Whitelisted (Policy + Reviewer)',
        value: funnel.whitelisted_only?.count ?? 0,
        color: isDeveloper ? BIAS_GRAPH_BLUE : BIAS_GRAPH_GREEN
      },
      {
        label: 'Residual Review Required',
        value: funnel.residual_review_required?.count ?? 0,
        color: isDeveloper ? BIAS_GRAPH_BLUE : BIAS_GRAPH_RED
      }
    ];
  }, [globalAggregateSummary, isDeveloper]);

  const governanceGateOverlay = useMemo((): GovernanceGateOverlay | null => {
    const summary = globalAggregateSummary;
    const density = summary?.sensitive_density;
    if (!density || typeof density !== 'object') return null;
    return {
      totalSensitiveMentions: Number(density.total_sensitive_mentions ?? 0),
      avgPerOutput: Number(density.avg_per_output ?? 0),
      minPerOutput: Number(density.min_per_output ?? 0),
      maxPerOutput: Number(density.max_per_output ?? 0)
    };
  }, [globalAggregateSummary]);

  const sensitiveAttributesTriggeredData = useMemo((): SensitiveAttributeTriggerItem[] | null => {
    const summary = globalAggregateSummary;
    const bySource = summary?.non_whitelisted_sensitive_by_source ?? summary?.sensitive_by_source;
    if (!bySource || typeof bySource !== 'object') return null;
    const totals: Record<string, number> = {};
    for (const source of ['prompt', 'metadata', 'output'] as const) {
      const map = (bySource as Record<string, unknown>)[source];
      if (map && typeof map === 'object') {
        for (const [attr, count] of Object.entries(map)) {
          const n = Number(count);
          if (!Number.isNaN(n)) totals[attr] = (totals[attr] ?? 0) + n;
        }
      }
    }
    const entries = Object.entries(totals)
      .map(([attribute, count]) => ({ attribute, count }))
      .sort((a, b) => b.count - a.count);
    return entries.length > 0 ? entries : null;
  }, [globalAggregateSummary]);

  const microeaiBiasScore = useMemo(() => {
    const summary = globalAggregateSummary;
    const score = summary?.microeai_bias_score;
    return typeof score === 'number' && !Number.isNaN(score) ? score : null;
  }, [globalAggregateSummary]);

  const tableEntries = useMemo(() => {
    if (requestRecords && requestRecords.length > 0) return requestRecords;
    return extractFirstLlmRequestData(projectResults) ?? [];
  }, [requestRecords, projectResults]);

  // Show charts whenever we have an aggregate summary; each chart shows its own empty state if needed
  if (!globalAggregateSummary) {
    return (
      <Typography variant="body2" color="text.secondary">
        No bias LLM data available for this project. Run bias analysis for this project to see charts here.
      </Typography>
    );
  }

  return (
    <Row className="g-4">
      <Col xs={12}>
        <Card className="shadow-sm border-0 overflow-visible" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Entity-Level Bias Status (Governance Gate)
            </Typography>
            <GovernanceGateChart
              data={governanceGateData}
              overlay={governanceGateOverlay}
              height={320}
              emptyMessage="No governance gate data available."
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12}>
        <Card className="shadow-sm border-0 overflow-visible" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <SensitiveAttributesFrequencyPanel
              title="Sensitive attributes"
              data={sensitiveAttributesTriggeredData}
              maxHeight={340}
              emptyMessage="No sensitive attributes detected."
              softPalette={softPalette}
              barsAllBlue={isDeveloper}
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card className="shadow-sm border-0 overflow-visible h-100" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Bias Metrics (Compliance vs Audit)
            </Typography>
            <ComplianceVsAuditChart
              data={complianceVsAuditData}
              microeaiBiasScore={microeaiBiasScore}
              height={320}
              emptyMessage="No compliance vs audit metrics available."
              softPalette={softPalette}
              barsAllBlue={isDeveloper}
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card className="shadow-sm border-0 overflow-visible h-100" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 d-flex flex-column">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Final Compliance Verdict
            </Typography>
            <div className="flex-grow-1">
              <VerdictBarChart
                data={verdictData}
                height={320}
                showRejectNote
                emptyMessage="No verdict distribution data available."
              />
            </div>
          </Card.Body>
        </Card>
      </Col>
      {tableEntries.length > 0 ? (
        <Col xs={12}>
          <BiasLlmRecordsTable entries={tableEntries} />
        </Col>
      ) : null}
    </Row>
  );
};

export default BiasLLMPlotsSection;
